import React, { FC } from 'react';
import { Category } from 'shared/types/product';
import Rating from 'components/commercetools-ui/atoms/rating';
import Typography from 'components/commercetools-ui/atoms/typography';
import Link from 'components/commercetools-ui/organisms/content/link';
import useClassNames from 'helpers/hooks/useClassNames';
import { variantDisplayNames } from 'helpers/seo/variant-seo';
import { CustomProductDetailsProps } from '..';
import { DEFAULT_LOCALE } from '../../../../../../helpers/constants/locale';
import { ProductJSONLD } from '../../../../../../helpers/structuredData/product';
import PriceSection from '../../product-details/components/price-section';

// Create a reviews link that points to the Reviews component section using a hash anchor
const reviewsLink = '#reviews';

type ProductInformationProps = Omit<CustomProductDetailsProps, 'onAddToCart' | 'content'> & {
  productContent?: React.ReactNode;
  category?: Category;
  shortDescription?: React.ReactNode;
  plainTextDescription?: string;
  origin?: string;
  extraCentAmount?: number;
};

const ProductInformation: FC<ProductInformationProps> = ({
  product,
  category,
  variant,
  plainTextDescription,
  description,
  inModalVersion,
  reviewListProps,
  origin,
  categoryCtaText,
  categoryCtaUrl,
  extraCentAmount,
}) => {

  const titleClassName = useClassNames(['break-normal mb-12', { 'body-text-x-large font-medium': !inModalVersion }]);

  // Calculate final price for ProductJSONLD
  const finalBasePrice = variant.price?.centAmount ? variant.price.centAmount : 0;

  return (
    <div>
      <ProductJSONLD
        product={product}
        variant={variant}
        description={plainTextDescription}
        origin={origin}
        locale={DEFAULT_LOCALE}
        finalBasePrice={finalBasePrice}
        ratingData={product?.reviewRatingStatistics}
        reviews={reviewListProps}
      />

      <div className="relative flex flex-col pr-40">
        {/* The page heading is the variant's display name — the same text as <title> and the structured
            data — like the standard PDP. Quick-view keeps it an h2. */}
        <Typography as={inModalVersion ? 'h2' : 'h1'} className={titleClassName}>
          {variantDisplayNames(product).get(variant.sku) ?? variant.name ?? product?.name}
        </Typography>

        {/* Add Rating component if review statistics are available */}
        {product.reviewRatingStatistics?.averageRating && product.reviewRatingStatistics?.count && (
          <div className="mt-8">
            <Rating
              rating={product.reviewRatingStatistics.averageRating}
              reviewCount={product.reviewRatingStatistics.count}
              reviewsLink={reviewsLink}
              textSize="large"
            />
          </div>
        )}
      </div>

      {/* PRICING SECTION */}
      <PriceSection
        variant={variant}
        extraCentAmount={extraCentAmount}
        pdpType="custom"
      />

      <div className="mb-24 hidden md:block">
        <Link
          key={category?.categoryId}
          target={{
            type: 'link',
            link: categoryCtaUrl ?? category?._url ?? '',
          }}
          className="text-sm !font-bold text-secondary underline-offset-4"
        >
          <span className="ml-4 underline-offset-4 hover:underline">{categoryCtaText ?? category?.name}</span>
          <span className="ml-4">&gt;</span>
        </Link>
      </div>
      {description && (
        <div className="mb-40 mt-48">
          {description}
        </div>
      )}
    </div>

  );
};

export default ProductInformation;
